import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Badge,
  Button,
  Modal,
  Form,
  Row,
  Col,
  InputGroup,
  FormControl,
  Spinner,
} from 'react-bootstrap';
import { 
  CalendarCheck, 
  Pencil, 
  Trash, 
  CameraVideo, 
  Search, 
  Download,
  FileEarmarkText,
  CheckCircle,
  XCircle
} from 'react-bootstrap-icons';
import { toast } from 'react-toastify';
import interviewService from '../../services/interviewService';
import studentService from '../../services/studentService';

const InterviewList = ({ interviews: initialInterviews, onInterviewUpdated }) => {
  const [interviews, setInterviews] = useState(initialInterviews || []);
  const [filteredInterviews, setFilteredInterviews] = useState(initialInterviews || []);
  const [showModal, setShowModal] = useState(false);
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [downloadingResumes, setDownloadingResumes] = useState(new Set());

  useEffect(() => {
    if (initialInterviews) {
      setInterviews(initialInterviews);
      setFilteredInterviews(initialInterviews);
    }
  }, [initialInterviews]);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredInterviews(interviews);
    } else {
      const lowerTerm = searchTerm.toLowerCase();
      setFilteredInterviews(
        interviews.filter(
          (interview) =>
            (interview.StudentName && interview.StudentName.toLowerCase().includes(lowerTerm)) ||
            (interview.StudentEmail && interview.StudentEmail.toLowerCase().includes(lowerTerm)) ||
            (interview.Interviewer && interview.Interviewer.toLowerCase().includes(lowerTerm)) ||
            (interview.Status && interview.Status.toLowerCase().includes(lowerTerm))
        )
      );
    }
  }, [searchTerm, interviews]);

  const getStatusVariant = useCallback(() => {
    return (status) => {
      const variants = {
        Invited: 'info',
        Scheduled: 'primary',
        Completed: 'success',
        Cancelled: 'danger',
        'No-Show': 'warning',
      };
      return variants[status] || 'secondary';
    };
  }, []);

  const handleStatusUpdate = useCallback((interview) => {
    setSelectedInterview(interview);
    setNewStatus(interview.Status);
    setShowModal(true);
  }, []);

  const submitUpdate = useCallback(async () => {
    if (!selectedInterview) return;
    try {
      await interviewService.updateStatus(selectedInterview.Id, newStatus);
      setInterviews((prev) =>
        prev.map((i) => (i.Id === selectedInterview.Id ? { ...i, Status: newStatus } : i))
      );
      toast.success('Interview status updated successfully');
      setShowModal(false);
      
      if (onInterviewUpdated) {
        onInterviewUpdated();
      }
    } catch (error) {
      console.error('Failed to update interview status:', error);
      toast.error('Failed to update interview status');
    }
  }, [newStatus, selectedInterview, onInterviewUpdated]);

  const handleDelete = useCallback(async (id, studentName) => {
    if (window.confirm(`Are you sure you want to delete the interview for ${studentName}?`)) {
      try {
        await interviewService.deleteInterview(id);
        setInterviews((prev) => prev.filter((i) => i.Id !== id));
        toast.success(`Interview for ${studentName} deleted successfully`);
        
        if (onInterviewUpdated) {
          onInterviewUpdated();
        }
      } catch (error) {
        console.error('Failed to delete interview:', error);
        toast.error('Failed to delete interview');
      }
    }
  }, [onInterviewUpdated]);

  const handleResumeDownload = useCallback(async (interview) => {
    if (!interview.StudentId) {
      toast.warning('Unable to download resume: Student ID not found');
      return;
    }

    setDownloadingResumes(prev => new Set(prev).add(interview.StudentId));
    
    try {
      console.log('📥 Downloading resume for student:', interview.StudentName);
      const result = await studentService.downloadResumeWithFeedback(
        interview.StudentId, 
        interview.StudentName
      );
      
      const cleanName = interview.StudentName.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_');
      const expectedFilename = result.filename || `${cleanName}.pdf`;
      
      toast.success(
        <div>
          <strong>Resume Downloaded!</strong>
          <br />
          <small>Student: {interview.StudentName}</small>
          <br />
          <small>File: {expectedFilename}</small>
        </div>
      );
    } catch (error) {
      console.error('❌ Resume download failed:', error);
      const errorMessage = error.message || 'Failed to download resume';
      
      // Check if it's a "no resume" error
      if (errorMessage.includes('not found') || errorMessage.includes('No resume')) {
        toast.warning(`No resume uploaded for ${interview.StudentName}`);
      } else {
        toast.error(
          <div>
            <strong>Download Failed</strong>
            <br />
            <small>Student: {interview.StudentName}</small>
            <br />
            <small>Error: {errorMessage}</small>
          </div>
        );
      }
    } finally {
      setDownloadingResumes(prev => {
        const newSet = new Set(prev);
        newSet.delete(interview.StudentId);
        return newSet;
      });
    }
  }, []);

  // Updated resume status badge function
  const getResumeStatusBadge = useCallback((interview) => {
    // If HasResume is explicitly defined in interview data
    if (interview.HasResume !== undefined) {
      if (interview.HasResume) {
        return (
          <Badge bg="success" className="d-flex align-items-center gap-1 small">
            <CheckCircle size={12} />
            Download
          </Badge>
        );
      } else {
        return (
          <Badge bg="secondary" className="d-flex align-items-center gap-1 small">
            <XCircle size={12} />
            Not Uploaded
          </Badge>
        );
      }
    }
    
    // If HasResume is not defined, show download option (assume resume exists unless proven otherwise)
    return (
      <Badge bg="info" className="d-flex align-items-center gap-1 small">
        <FileEarmarkText size={12} />
        Download
      </Badge>
    );
  }, []);

  // Check if resume download should be available
  const shouldShowDownloadButton = useCallback((interview) => {
    // Show download button if:
    // 1. HasResume is explicitly true
    // 2. HasResume is undefined (assume resume exists)
    // Don't show if HasResume is explicitly false
    return interview.HasResume !== false;
  }, []);

  const statusVariantFn = getStatusVariant();

  // Determine if we need scrolling based on number of rows
  const needsScrolling = filteredInterviews.length > 10;
  const maxHeight = needsScrolling ? '600px' : 'auto';

  return (
    <>
      {/* Header with search */}
      <Row className="align-items-center bg-info mx-0 rounded-top p-2">
        <Col xs={12} md="auto" className="d-flex align-items-center">
          <h5 className="mb-0 d-flex align-items-center text-white fw-bold">
            <CalendarCheck className="me-2 fs-4" />
            Scheduled Interviews
          </h5>
          <Badge bg="primary" pill className="ms-2">
            {filteredInterviews.length}
          </Badge>
        </Col>

        <Col className="d-none d-md-block" />

        <Col xs={12} md={6} lg={5} className="ms-auto">
          <InputGroup className="shadow-sm">
            <InputGroup.Text className="bg-light border-end-0">
              <Search className="text-muted" />
            </InputGroup.Text>
            <FormControl
              placeholder="Search interviews by student, email, interviewer, or status..."
              aria-label="Search interviews"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoComplete="off"
              className="border-start-0"
            />
          </InputGroup>
        </Col>
      </Row>

      {/* Enhanced scroll container with dynamic height and sticky header */}
      <div
        className="table-responsive border border-top-0"
        style={{
          maxHeight: maxHeight,
          overflowY: needsScrolling ? 'auto' : 'visible',
          overflowX: 'auto',
          position: 'relative',
        }}
      >
        <Table
          striped
          bordered
          hover
          className="mb-0 align-middle text-nowrap"
          style={{ fontSize: '0.9rem' }}
        >
          <thead
            className="table-info"
            style={{
              position: 'sticky',
              top: 0,
              zIndex: 10,
              boxShadow: needsScrolling ? '0 2px 4px rgba(0,0,0,0.1)' : 'none',
            }}
          >
            <tr>
              <th className="fw-bold">Student</th>
              <th className="fw-bold">Email</th>
              <th className="fw-bold">Slot Date & Time</th>
              <th className="fw-bold">Interviewer</th>
              <th className="fw-bold text-center">Status</th>
              <th className="fw-bold text-center">Resume</th>
              <th className="fw-bold text-center">Meeting Link</th>
              <th className="fw-bold text-center" style={{ width: 160 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredInterviews.length === 0 ? (
              <tr>
                <td colSpan="8" className="text-center py-5 text-muted">
                  <div>
                    <Search size={48} className="text-muted mb-3" />
                    <h6>No interviews found</h6>
                    <p>Try adjusting your search terms or check back later</p>
                  </div>
                </td>
              </tr>
            ) : (
              filteredInterviews.map((interview, index) => (
                <tr 
                  key={interview.Id} 
                  className={index % 2 === 0 ? 'table-light' : ''}
                  style={{ 
                    transition: 'background-color 0.2s ease',
                  }}
                >
                  <td>
                    <div className="fw-semibold text-truncate" style={{ maxWidth: '150px' }}>
                      {interview.StudentName}
                    </div>
                  </td>
                  <td>
                    <small className="text-muted text-truncate d-block" style={{ maxWidth: '180px' }}>
                      {interview.StudentEmail}
                    </small>
                  </td>
                  <td>
                    <div className="small fw-medium">
                      {interview.SlotDateTime
                        ? new Date(interview.SlotDateTime).toLocaleString('en-IN', {
                            timeZone: 'Asia/Kolkata',
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })
                        : '-'}
                    </div>
                  </td>
                  <td>
                    <span className="text-truncate d-block" style={{ maxWidth: '120px' }}>
                      {interview.Interviewer || '-'}
                    </span>
                  </td>
                  <td className="text-center">
                    <Badge bg={statusVariantFn(interview.Status)} pill className="px-2">
                      {interview.Status}
                    </Badge>
                  </td>
                  <td className="text-center">
                    <div className="d-flex align-items-center justify-content-center gap-2">
                      {getResumeStatusBadge(interview)}
                      {shouldShowDownloadButton(interview) && (
                        <Button
                          variant={interview.HasResume === false ? "outline-secondary" : "outline-success"}
                          size="sm"
                          onClick={() => handleResumeDownload(interview)}
                          disabled={downloadingResumes.has(interview.StudentId) || interview.HasResume === false}
                          title={
                            interview.HasResume === false 
                              ? "No resume uploaded" 
                              : `Download ${interview.StudentName.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_')}.pdf`
                          }
                          className="d-flex align-items-center p-1"
                        >
                          {downloadingResumes.has(interview.StudentId) ? (
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                            />
                          ) : (
                            <Download size={14} />
                          )}
                        </Button>
                      )}
                    </div>
                  </td>
                  <td className="text-center">
                    {interview.MeetingLink ? (
                      <Button
                        as="a"
                        href={interview.MeetingLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="sm"
                        variant="outline-primary"
                        aria-label={`Join meeting for ${interview.StudentName}`}
                        className="d-flex align-items-center gap-1 mx-auto"
                        style={{ width: 'fit-content' }}
                      >
                        <CameraVideo size={14} />
                        <span className="small">Join</span>
                      </Button>
                    ) : (
                      <Badge bg="light" text="dark" className="small">No Link</Badge>
                    )}
                  </td>
                  <td>
                    <div className="d-flex gap-1 justify-content-center flex-nowrap">
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={() => handleStatusUpdate(interview)}
                        aria-label={`Update interview status for ${interview.StudentName}`}
                        title="Update Status"
                        className="p-1"
                      >
                        <Pencil size={14} />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => handleDelete(interview.Id, interview.StudentName)}
                        aria-label={`Delete interview for ${interview.StudentName}`}
                        title="Delete Interview"
                        className="p-1"
                      >
                        <Trash size={14} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      {/* Scroll indicator for large tables */}
      {needsScrolling && filteredInterviews.length > 0 && (
        <div className="text-center py-2 bg-light border border-top-0 rounded-bottom">
          <small className="text-muted">
            <Badge bg="secondary" className="me-2">{filteredInterviews.length} interviews</Badge>
            {filteredInterviews.length > 10 && "Scroll vertically to see more rows"}
          </small>
        </div>
      )}

      {/* Status Update Modal */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        centered
        backdrop="static"
        keyboard={false}
        aria-labelledby="update-status-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title id="update-status-modal">
            <Pencil className="me-2" />
            Update Interview Status
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedInterview && (
            <div className="mb-3">
              <strong>Student:</strong> {selectedInterview.StudentName}
              <br />
              <small className="text-muted">{selectedInterview.StudentEmail}</small>
            </div>
          )}
          <Form>
            <Form.Floating>
              <Form.Select
                id="interviewStatus"
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                aria-label="Select interview status"
              >
                <option value="Invited">Invited</option>
                <option value="Scheduled">Scheduled</option>
                <option value="Completed">Completed</option>
                <option value="Cancelled">Cancelled</option>
                <option value="No-Show">No-Show</option>
              </Form.Select>
              <Form.Label htmlFor="interviewStatus">Interview Status</Form.Label>
            </Form.Floating>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submitUpdate}>
            <CheckCircle className="me-1" />
            Update Status
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default InterviewList;
